import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Phone, MapPin, Mail, Calendar, Users, Sparkles, ChevronDown, Trees, Heart, Package, Church } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { mockData } from '../data/mock';
import Nav from '../components/Nav';
import TestimonialStrip from '../components/TestimonialStrip';

const Home = () => {
  const [activeSection, setActiveSection] = useState('');
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    // Route special IDs to dedicated pages
    const routes = { contact: '/contact', gallery: '/gallery', events: '/events' };
    if (routes[id]) {
      window.location.href = routes[id];
      return;
    }
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-cream">
      <Nav transparent />

      {/* Hero Section - Full Bleed Image */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background image */}
        <div className="absolute inset-0">
          <img
            src="/hero/hero-main.jpg"
            alt="Queensland Ranch Estate"
            className="w-full h-full object-cover"
          />
          {/* Stronger cinematic gradient overlay - darker top, softer middle, dark bottom */}
          <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(10,16,32,0.85)_0%,rgba(10,16,32,0.55)_45%,rgba(10,16,32,0.85)_100%)]" />
          {/* Extra top-half darkening for editorial feel */}
          <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(10,16,32,0.35)_0%,transparent_55%)]" />
          {/* Subtle radial vignette for focus on text */}
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,rgba(10,16,32,0.45)_75%)]" />
        </div>

        {/* Gold corner flourishes */}
        <div className="absolute top-28 left-10 w-24 h-24 border-l-2 border-t-2 border-gold/70 hidden md:block" aria-hidden="true" />
        <div className="absolute top-28 right-10 w-24 h-24 border-r-2 border-t-2 border-gold/70 hidden md:block" aria-hidden="true" />
        <div className="absolute bottom-24 left-10 w-24 h-24 border-l-2 border-b-2 border-gold/70 hidden md:block" aria-hidden="true" />
        <div className="absolute bottom-24 right-10 w-24 h-24 border-r-2 border-b-2 border-gold/70 hidden md:block" aria-hidden="true" />

        <div className="relative z-10 text-center text-white px-6 max-w-5xl py-32">
          {/* Urgency badge */}
          <div className="inline-flex items-center gap-2 px-5 py-2 mb-10 rounded-full bg-gold text-navy text-xs md:text-sm font-semibold tracking-[0.2em] uppercase shadow-2xl animate-fade-in" data-testid="hero-urgency-badge">
            <span className="w-2 h-2 rounded-full bg-navy animate-pulse" />
            Limited 2026 Dates Remaining
          </div>

          <h1 className="font-serif text-4xl md:text-6xl lg:text-7xl font-bold mb-3 leading-[1.05] drop-shadow-2xl">
            A Private <span className="text-gold italic">Luxury Estate</span>
            <span className="block mt-2">for Unforgettable Weddings</span>
          </h1>

          {/* Gold divider */}
          <div className="flex items-center justify-center gap-3 my-6">
            <div className="h-px w-16 md:w-24 bg-gradient-to-r from-transparent to-gold" />
            <div className="w-1.5 h-1.5 rotate-45 bg-gold" />
            <div className="h-px w-16 md:w-24 bg-gradient-to-l from-transparent to-gold" />
          </div>

          <p className="font-serif text-xl md:text-3xl italic text-gold mb-3 tracking-wide drop-shadow-lg">
            Where Your Dream Wedding Comes to Life
          </p>
          <p className="text-sm md:text-base text-white/80 mb-12 tracking-[0.25em] uppercase drop-shadow-lg">
            Serving El Paso &amp; Southern New Mexico
          </p>

          {/* Button hierarchy: ONE dominant primary, outline secondary */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-6">
            <Button 
              onClick={() => scrollToSection('contact')}
              className="bg-gold hover:bg-gold/90 text-navy font-bold text-lg md:text-xl px-10 md:px-14 py-7 md:py-8 h-auto tracking-wide shadow-[0_8px_30px_rgba(184,155,92,0.45)] hover:shadow-[0_12px_40px_rgba(184,155,92,0.65)] hover:-translate-y-0.5 transition-all"
              data-testid="hero-check-availability"
            >
              <Calendar className="mr-2 h-5 w-5 md:h-6 md:w-6" />
              Check Availability
            </Button>
            <Button 
              onClick={() => scrollToSection('contact')}
              size="sm"
              variant="outline"
              className="bg-transparent border-white/50 text-white hover:bg-white/10 hover:border-white hover:text-white text-sm px-6 py-4 tracking-wide"
              data-testid="hero-schedule-tour"
            >
              Schedule a Private Tour
            </Button>
          </div>

          {/* Micro-trust line under buttons */}
          <p className="text-white/70 text-xs md:text-sm tracking-wide mb-16 italic" data-testid="hero-micro-trust">
            <span className="text-gold">✦</span>&nbsp; Limited 2026 dates available &nbsp;&middot;&nbsp; Private tours by appointment only
          </p>
        </div>

        <button 
          onClick={() => scrollToSection('logo-band')}
          className="absolute bottom-6 left-1/2 transform -translate-x-1/2 text-gold/80 hover:text-gold animate-bounce z-10"
          aria-label="Scroll down"
        >
          <ChevronDown className="h-8 w-8" />
        </button>
      </section>

      {/* Logo Band - Extra spacing top to visually distance from Emergent badge */}
      <section id="logo-band" className="relative py-20 md:py-24 bg-gradient-to-b from-[#0B1224] via-navy to-[#0B1224] overflow-hidden">
        {/* Luxe texture: radial gold glow + subtle noise */}
        <div className="absolute inset-0 opacity-20 pointer-events-none">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(184,155,92,0.25)_0%,transparent_55%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom,rgba(184,155,92,0.15)_0%,transparent_60%)]" />
        </div>
        {/* Thin gold top border */}
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold/60 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold/60 to-transparent" />

        <div className="container mx-auto px-6 text-center relative z-10">
          <img 
            src="https://customer-assets.emergentagent.com/job_qranch-events/artifacts/vdltfvkd_57C13979-2E1E-48A1-8B70-9ADF9C88F48B.png"
            alt="Queensland Ranch Logo"
            className="w-48 md:w-64 mx-auto mb-6 drop-shadow-2xl"
          />
          <div className="flex items-center justify-center gap-3 mx-auto">
            <div className="h-px w-12 bg-gradient-to-r from-transparent to-gold" />
            <p className="text-gold text-xs md:text-sm tracking-[0.3em] uppercase">
              Anthony, New Mexico &middot; Est. Family Owned
            </p>
            <div className="h-px w-12 bg-gradient-to-l from-transparent to-gold" />
          </div>
        </div>
      </section>

      {/* What We Host - Preview Cards linking to dedicated pages */}
      <section className="py-24 bg-cream texture-linen" data-testid="what-we-host">
        <div className="container mx-auto px-6">
          <div className="text-center mb-14 max-w-3xl mx-auto">
            <p className="text-gold text-sm tracking-[0.3em] uppercase mb-3">What We Host</p>
            <h2 className="font-serif text-3xl md:text-5xl font-bold text-navy mb-4">
              Celebrations of <span className="text-gold italic">Every Kind</span>
            </h2>
            <div className="flex items-center justify-center gap-3 mx-auto mb-5">
              <div className="h-px w-16 bg-gradient-to-r from-transparent to-gold" />
              <div className="w-1.5 h-1.5 rotate-45 bg-gold" />
              <div className="h-px w-16 bg-gradient-to-l from-transparent to-gold" />
            </div>
            <p className="text-slate-600 text-lg italic">Serving El Paso &amp; Southern New Mexico from our private 7-acre estate.</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 max-w-6xl mx-auto">
            {[
              { to: '/weddings', label: 'Weddings', desc: 'Willow-tree ceremonies, string-lit receptions.', icon: Heart },
              { to: '/quinceaneras', label: 'Quinceañeras', desc: 'The vals, the corte, the celebration.', icon: Sparkles },
              { to: '/events', label: 'Events', desc: 'Birthdays, pool parties, graduations, corporate.', icon: Users },
              { to: '/gallery', label: 'The Gallery', desc: 'A visual tour of the estate.', icon: Trees },
            ].map((c, i) => (
              <Link key={i} to={c.to} className="group" data-testid={`hostcard-${c.label.toLowerCase()}`}>
                <div className="bg-ivory border border-gold/25 rounded-lg p-7 shadow-sm hover:shadow-2xl hover:border-gold/60 transition-all duration-500 hover:-translate-y-1 h-full flex flex-col">
                  <div className="w-12 h-12 rounded-full bg-navy flex items-center justify-center ring-2 ring-gold/40 ring-offset-4 ring-offset-ivory mb-5">
                    <c.icon className="h-5 w-5 text-gold" />
                  </div>
                  <h3 className="font-serif text-xl text-navy font-bold mb-2">{c.label}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed flex-grow">{c.desc}</p>
                  <p className="text-gold text-xs tracking-[0.2em] uppercase mt-5 group-hover:tracking-[0.28em] transition-all">Explore &rarr;</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 bg-ivory texture-linen">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="font-serif text-4xl md:text-5xl font-bold text-slate-800 mb-4">
                About Queensland Ranch
              </h2>
              <div className="flex items-center justify-center gap-3 mx-auto mb-6">
              <div className="h-px w-16 bg-gradient-to-r from-transparent to-gold" />
              <div className="w-1.5 h-1.5 rotate-45 bg-gold" />
              <div className="h-px w-16 bg-gradient-to-l from-transparent to-gold" />
            </div>
              <p className="text-xl text-slate-600 max-w-3xl mx-auto">
                {mockData.venue.description}
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <img 
                  src={mockData.venue.aboutImage} 
                  alt="Queensland Ranch Venue"
                  className="rounded-lg shadow-2xl w-full h-[500px] object-cover"
                />
              </div>
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <MapPin className="h-6 w-6 text-gold mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-lg text-slate-800 mb-2">Prime Location</h3>
                    <p className="text-slate-600">
                      {mockData.venue.address}
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <Sparkles className="h-6 w-6 text-gold mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-lg text-slate-800 mb-2">Stunning Architecture</h3>
                    <p className="text-slate-600">
                      Our Mediterranean-style venue features elegant white architecture with terracotta roofing, surrounded by lush landscapes and modern amenities.
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <Users className="h-6 w-6 text-gold mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-lg text-slate-800 mb-2">Flexible Capacity</h3>
                    <p className="text-slate-600">
                      Accommodate intimate gatherings to grand celebrations across our beautiful outdoor spaces.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Story / Family Bio Section */}
      <section id="our-story" className="py-24 bg-ivory texture-linen relative overflow-hidden">
        {/* Subtle watermark monogram in background */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-[0.04]">
          <span className="font-serif text-[26rem] text-navy leading-none select-none">QR</span>
        </div>

        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-gold text-sm tracking-[0.3em] uppercase mb-3">
              {mockData.venue.bio.eyebrow}
            </p>
            <h2 className="font-serif text-4xl md:text-5xl font-bold text-navy mb-4">
              A Family <span className="text-gold italic">Legacy</span> of Celebration
            </h2>
            <div className="flex items-center justify-center gap-3 mx-auto mb-10">
              <div className="h-px w-16 bg-gradient-to-r from-transparent to-gold" />
              <div className="w-1.5 h-1.5 rotate-45 bg-gold" />
              <div className="h-px w-16 bg-gradient-to-l from-transparent to-gold" />
            </div>

            <div className="space-y-6 text-slate-700 text-lg leading-relaxed">
              {mockData.venue.bio.paragraphs.map((p, i) => (
                <p key={i} className={i === 0 ? 'text-xl' : ''}>
                  {p}
                </p>
              ))}
            </div>

            <p
              className="mt-10 font-serif italic text-gold text-lg tracking-wide"
              dangerouslySetInnerHTML={{ __html: mockData.venue.bio.signature }}
            />

            <div className="mt-10 flex justify-center">
              <Button
                onClick={() => scrollToSection('contact')}
                size="lg"
                className="bg-navy hover:bg-navy/90 text-gold border border-gold/60 tracking-wide px-10 py-6 shadow-lg"
                data-testid="our-story-cta"
              >
                <Calendar className="mr-2 h-5 w-5" />
                Schedule a Tour
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Signature Experiences Section */}
      <section id="signature" className="py-24 bg-cream texture-linen">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-gold text-sm tracking-[0.3em] uppercase mb-3">What Sets Us Apart</p>
            <h2 className="font-serif text-4xl md:text-5xl font-bold text-navy mb-4">
              Signature <span className="text-gold italic">Experiences</span>
            </h2>
            <div className="flex items-center justify-center gap-3 mx-auto mb-6">
              <div className="h-px w-16 bg-gradient-to-r from-transparent to-gold" />
              <div className="w-1.5 h-1.5 rotate-45 bg-gold" />
              <div className="h-px w-16 bg-gradient-to-l from-transparent to-gold" />
            </div>
          </div>

          {/* Aquatic Oasis */}
          <div className="max-w-6xl mx-auto mb-24" data-testid="signature-pool">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div className="grid grid-cols-2 gap-3">
                {mockData.signatureExperiences.pool.images.map((src, idx) => (
                  <div
                    key={idx}
                    className={`relative overflow-hidden rounded-lg shadow-xl ${
                      idx === 0 ? 'col-span-2 aspect-[16/9]' : 'aspect-square'
                    }`}
                  >
                    <img
                      src={src}
                      alt={`Pool view ${idx + 1}`}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                    />
                  </div>
                ))}
              </div>
              <div>
                <p className="text-gold text-xs tracking-[0.3em] uppercase mb-3">Pool Parties &amp; Summer Celebrations</p>
                <h3 className="font-serif text-3xl md:text-4xl font-bold text-navy mb-2">
                  {mockData.signatureExperiences.pool.title}
                </h3>
                <p className="font-serif text-lg italic text-gold mb-6">
                  {mockData.signatureExperiences.pool.tagline}
                </p>
                <p className="text-slate-700 mb-6 leading-relaxed">
                  {mockData.signatureExperiences.pool.description}
                </p>
                <ul className="space-y-3 mb-6">
                  {mockData.signatureExperiences.pool.features.map((f, i) => (
                    <li key={i} className="flex items-start text-slate-700">
                      <span className="w-1.5 h-1.5 bg-gold rotate-45 mr-3 mt-2 flex-shrink-0" />
                      <span className="leading-relaxed">{f}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-slate-600 italic border-l-2 border-gold/60 pl-4 leading-relaxed">
                  {mockData.signatureExperiences.pool.footer}
                </p>
              </div>
            </div>
          </div>

          {/* Nighttime Ambience */}
          <div className="max-w-6xl mx-auto" data-testid="signature-night">
            <div className="relative rounded-2xl overflow-hidden bg-gradient-to-br from-[#0B1224] via-navy to-[#0B1224] p-10 md:p-16 shadow-2xl border border-gold/30">
              {/* Ambient glow */}
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[400px] bg-[radial-gradient(ellipse,rgba(184,155,92,0.20)_0%,transparent_60%)] pointer-events-none" />
              {/* Gold corner flourishes */}
              <div className="absolute top-6 left-6 w-16 h-16 border-l-2 border-t-2 border-gold/40" aria-hidden="true" />
              <div className="absolute top-6 right-6 w-16 h-16 border-r-2 border-t-2 border-gold/40" aria-hidden="true" />
              <div className="absolute bottom-6 left-6 w-16 h-16 border-l-2 border-b-2 border-gold/40" aria-hidden="true" />
              <div className="absolute bottom-6 right-6 w-16 h-16 border-r-2 border-b-2 border-gold/40" aria-hidden="true" />

              <div className="text-center max-w-3xl mx-auto relative z-10">
                <p className="text-gold text-xs tracking-[0.3em] uppercase mb-3">After Sunset at the Ranch</p>
                <h3 className="font-serif text-3xl md:text-4xl font-bold text-white mb-2">
                  {mockData.signatureExperiences.night.title}
                </h3>
                <p className="font-serif text-lg italic text-gold mb-8">
                  {mockData.signatureExperiences.night.tagline}
                </p>
                <p className="text-white/85 leading-relaxed mb-10">
                  {mockData.signatureExperiences.night.description}
                </p>
                <div className="grid sm:grid-cols-2 gap-4 max-w-2xl mx-auto text-left">
                  {mockData.signatureExperiences.night.highlights.map((h, i) => (
                    <div
                      key={i}
                      className="flex items-start bg-white/5 border border-gold/20 rounded-lg p-4"
                    >
                      <span className="w-1.5 h-1.5 bg-gold rotate-45 mr-3 mt-2 flex-shrink-0" />
                      <span className="text-white/90 text-sm leading-relaxed">{h}</span>
                    </div>
                  ))}
                </div>

                {/* Night photo gallery */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-10">
                  {mockData.signatureExperiences.night.images.map((src, idx) => (
                    <div
                      key={idx}
                      className="relative overflow-hidden rounded-lg aspect-square border border-gold/20 shadow-xl group"
                    >
                      <img
                        src={src}
                        alt={`Nighttime ambience ${idx + 1}`}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Event Types Section */}
      <section id="events" className="py-20 bg-sage/5">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="font-serif text-4xl md:text-5xl font-bold text-slate-800 mb-4">
              Perfect for Every Occasion
            </h2>
            <div className="flex items-center justify-center gap-3 mx-auto mb-6">
              <div className="h-px w-16 bg-gradient-to-r from-transparent to-gold" />
              <div className="w-1.5 h-1.5 rotate-45 bg-gold" />
              <div className="h-px w-16 bg-gradient-to-l from-transparent to-gold" />
            </div>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              From intimate celebrations to grand affairs, Queensland Ranch provides the perfect backdrop for your special moments
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {mockData.eventTypes.map((event, index) => (
              <Card 
                key={index}
                className="group hover:shadow-2xl transition-all duration-300 overflow-hidden border-0"
              >
                <div className="relative h-64 overflow-hidden">
                  <img 
                    src={event.image} 
                    alt={event.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                  <div className="absolute bottom-4 left-4 right-4">
                    <h3 className="font-serif text-2xl font-bold text-white mb-2">
                      {event.title}
                    </h3>
                  </div>
                </div>
                <CardContent className="p-6">
                  <p className="text-slate-600 mb-4">{event.description}</p>
                  <ul className="space-y-2">
                    {event.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center text-sm text-slate-700">
                        <span className="w-1.5 h-1.5 bg-gold rotate-45 mr-2 mt-1.5 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Custom Quote Section */}
      <section id="custom-quote" className="py-24 bg-gradient-to-b from-[#0B1224] via-navy to-[#0B1224] relative overflow-hidden" data-testid="custom-quote-section">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[500px] bg-[radial-gradient(ellipse,rgba(184,155,92,0.18)_0%,transparent_60%)] pointer-events-none" />
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold/50 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold/50 to-transparent" />
        <div className="container mx-auto px-6 relative z-10 text-center max-w-3xl">
          <p className="text-gold text-sm tracking-[0.3em] uppercase mb-3">Tailored to You</p>
          <h2 className="font-serif text-4xl md:text-5xl font-bold text-white mb-4">
            Every Celebration, <span className="text-gold italic">Personally Crafted</span>
          </h2>
          <div className="flex items-center justify-center gap-3 mx-auto mb-6">
            <div className="h-px w-16 bg-gradient-to-r from-transparent to-gold" />
            <div className="w-1.5 h-1.5 rotate-45 bg-gold" />
            <div className="h-px w-16 bg-gradient-to-l from-transparent to-gold" />
          </div>
          <p className="text-lg text-white/75 italic mb-10">
            No two events are alike. Share your date, guest count and vision, and Mary will personally prepare a custom quote for your wedding, quinceañera or private event.
          </p>
          <Button
            onClick={() => scrollToSection('contact')}
            className="bg-gold hover:bg-gold/90 text-navy font-semibold tracking-wide px-10 py-6 h-auto shadow-lg"
            data-testid="custom-quote-cta"
          >
            <Calendar className="mr-2 h-5 w-5" />
            Request a Custom Quote
          </Button>
        </div>
      </section>

      {/* Real Weddings Showcase */}
      <section id="real-weddings" className="py-20 bg-cream texture-linen relative overflow-hidden">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <p className="text-gold text-sm tracking-[0.3em] uppercase mb-3">Real Couples · Real Moments</p>
            <h2 className="font-serif text-4xl md:text-5xl font-bold text-navy mb-4">
              Real Weddings at <span className="text-gold italic">Queensland Ranch</span>
            </h2>
            <div className="flex items-center justify-center gap-3 mx-auto mb-6">
              <div className="h-px w-16 bg-gradient-to-r from-transparent to-gold" />
              <div className="w-1.5 h-1.5 rotate-45 bg-gold" />
              <div className="h-px w-16 bg-gradient-to-l from-transparent to-gold" />
            </div>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto italic">
              A glimpse into the unforgettable evenings our couples have created on our grounds.
            </p>
          </div>

          {/* Featured hero photo + supporting thumbs grid */}
          <div className="max-w-6xl mx-auto" data-testid="real-weddings-grid">
            {/* Featured large daytime hero */}
            <div className="relative overflow-hidden rounded-lg shadow-2xl border border-gold/20 group mb-3">
              <img
                src={mockData.realWeddings[0]}
                alt="Featured real wedding at Queensland Ranch"
                className="w-full h-[280px] md:h-[450px] object-cover group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-navy/70 via-transparent to-transparent" />
              <div className="absolute bottom-5 left-5 md:bottom-7 md:left-7 text-white">
                <p className="text-gold text-xs tracking-[0.25em] uppercase mb-1">Featured Day</p>
                <p className="font-serif italic text-xl md:text-2xl">An Afternoon of Elegance</p>
              </div>
            </div>

            {/* 3-column supporting grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {mockData.realWeddings.slice(1).map((src, idx) => (
                <div
                  key={idx}
                  className="relative overflow-hidden rounded-lg shadow-xl border border-gold/20 aspect-square group"
                >
                  <img
                    src={src}
                    alt={`Real wedding moment ${idx + 2}`}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="mt-12 text-center">
            <Button
              onClick={() => scrollToSection('contact')}
              size="lg"
              className="bg-navy hover:bg-navy/90 text-gold border border-gold/60 tracking-wide px-10 py-6 shadow-lg"
              data-testid="real-weddings-cta"
            >
              <Calendar className="mr-2 h-5 w-5" />
              Schedule a Tour
            </Button>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section id="gallery" className="py-20 bg-ivory texture-linen">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="font-serif text-4xl md:text-5xl font-bold text-slate-800 mb-4">
              Explore Our Venue
            </h2>
            <div className="flex items-center justify-center gap-3 mx-auto mb-6">
              <div className="h-px w-16 bg-gradient-to-r from-transparent to-gold" />
              <div className="w-1.5 h-1.5 rotate-45 bg-gold" />
              <div className="h-px w-16 bg-gradient-to-l from-transparent to-gold" />
            </div>
            <p className="text-xl text-slate-600">
              Take a visual journey through our stunning property
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 max-w-6xl mx-auto">
            {mockData.gallery.map((image, index) => (
              <div 
                key={index}
                className="relative overflow-hidden rounded-lg aspect-square"
              >
                <img 
                  src={image.url} 
                  alt={image.alt}
                  className="w-full h-full object-cover"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Amenities Section */}
      <section id="amenities" className="py-20 bg-terracotta/5">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="font-serif text-4xl md:text-5xl font-bold text-slate-800 mb-4">
              World-Class Amenities
            </h2>
            <div className="flex items-center justify-center gap-3 mx-auto mb-6">
              <div className="h-px w-16 bg-gradient-to-r from-transparent to-gold" />
              <div className="w-1.5 h-1.5 rotate-45 bg-gold" />
              <div className="h-px w-16 bg-gradient-to-l from-transparent to-gold" />
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {mockData.amenities.map((amenity, index) => (
              <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-shadow duration-300">
                <CardContent className="p-6">
                  <div className="flex items-start space-x-4">
                    <div className="bg-navy/10 p-3 rounded-lg border border-gold/30">
                      <amenity.icon className="h-6 w-6 text-gold" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-lg text-slate-800 mb-2">
                        {amenity.title}
                      </h3>
                      <p className="text-slate-600 text-sm">
                        {amenity.description}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section - using shared TestimonialStrip */}
      <TestimonialStrip title="Loved by Real Couples" accent="& Families" />

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-sage/10">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="font-serif text-4xl md:text-5xl font-bold text-slate-800 mb-4">
                Let's Plan Your Event
              </h2>
              <div className="flex items-center justify-center gap-3 mx-auto mb-6">
              <div className="h-px w-16 bg-gradient-to-r from-transparent to-gold" />
              <div className="w-1.5 h-1.5 rotate-45 bg-gold" />
              <div className="h-px w-16 bg-gradient-to-l from-transparent to-gold" />
            </div>
              <p className="text-xl text-slate-600">
                Contact us today to schedule a tour or discuss your special occasion
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <Card className="border border-gold/20 shadow-xl hover:shadow-2xl hover:border-gold/50 transition-all duration-300 bg-white">
                <CardContent className="p-8 text-center">
                  <div className="bg-navy w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ring-2 ring-gold/40 ring-offset-4 ring-offset-white">
                    <Phone className="h-8 w-8 text-gold" />
                  </div>
                  <h3 className="font-serif text-xl text-navy mb-3 tracking-wide">Call Us</h3>
                  <a 
                    href={`tel:${mockData.contact.phone}`} 
                    className="text-lg text-slate-700 hover:text-gold transition-colors font-medium"
                  >
                    {mockData.contact.phone}
                  </a>
                  <p className="text-sm text-slate-500 mt-2">Mon-Sun: 9am-6pm</p>
                </CardContent>
              </Card>

              <Card className="border border-gold/20 shadow-xl hover:shadow-2xl hover:border-gold/50 transition-all duration-300 bg-white">
                <CardContent className="p-8 text-center">
                  <div className="bg-navy w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ring-2 ring-gold/40 ring-offset-4 ring-offset-white">
                    <Mail className="h-8 w-8 text-gold" />
                  </div>
                  <h3 className="font-serif text-xl text-navy mb-3 tracking-wide">Email Us</h3>
                  <a 
                    href={`mailto:${mockData.contact.email}`} 
                    className="text-lg text-slate-700 hover:text-gold transition-colors font-medium break-all"
                  >
                    {mockData.contact.email}
                  </a>
                  <p className="text-sm text-slate-500 mt-2">We'll respond within 24hrs</p>
                </CardContent>
              </Card>

              <Card className="border border-gold/20 shadow-xl hover:shadow-2xl hover:border-gold/50 transition-all duration-300 bg-white">
                <CardContent className="p-8 text-center">
                  <div className="bg-navy w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ring-2 ring-gold/40 ring-offset-4 ring-offset-white">
                    <MapPin className="h-8 w-8 text-gold" />
                  </div>
                  <h3 className="font-serif text-xl text-navy mb-3 tracking-wide">Visit Us</h3>
                  <p className="text-lg text-slate-700 font-medium">{mockData.contact.address}</p>
                  <p className="text-sm text-slate-500 mt-2">Anthony, New Mexico</p>
                </CardContent>
              </Card>
            </div>

            <div className="mt-12 text-center">
              <p className="text-slate-600 mb-6 italic">
                Ready to book your special event? Reach out to us directly via phone or email.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a 
                  href={`tel:${mockData.contact.phone}`}
                  className="inline-flex items-center justify-center px-8 py-4 bg-navy text-gold font-semibold rounded-lg border border-gold/60 hover:bg-gold hover:text-navy hover:border-gold transition-all tracking-wide shadow-lg"
                >
                  <Phone className="mr-2 h-5 w-5" />
                  Call Now
                </a>
                <a 
                  href={`mailto:${mockData.contact.email}`}
                  className="inline-flex items-center justify-center px-8 py-4 bg-white text-navy font-semibold rounded-lg border border-navy/40 hover:bg-navy hover:text-gold hover:border-navy transition-all tracking-wide"
                >
                  <Mail className="mr-2 h-5 w-5" />
                  Send Email
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gradient-to-b from-[#0B1224] to-navy text-white py-14 border-t-2 border-gold/40 relative overflow-hidden">
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[800px] h-[300px] bg-[radial-gradient(ellipse,rgba(184,155,92,0.12)_0%,transparent_60%)] pointer-events-none" />
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-3 gap-8 mb-8">
              <div>
                <h3 className="font-serif text-2xl font-bold mb-4 text-gold">Queensland Ranch</h3>
                <p className="text-slate-300">
                  Creating unforgettable memories in the heart of New Mexico
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-lg mb-4">Quick Links</h4>
                <ul className="space-y-2">
                  {mockData.navigation.map((item) => (
                    <li key={item.id}>
                      <button
                        onClick={() => scrollToSection(item.id)}
                        className="text-slate-300 hover:text-gold transition-colors"
                      >
                        {item.label}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-lg mb-4">Contact</h4>
                <ul className="space-y-2 text-slate-300">
                  <li>{mockData.contact.phone}</li>
                  <li>{mockData.contact.email}</li>
                  <li>{mockData.contact.address}</li>
                </ul>
              </div>
            </div>
            <div className="border-t border-gold/30 pt-8 text-center text-slate-400">
              <p>&copy; {new Date().getFullYear()} Queensland Ranch. All rights reserved.</p>
              <p className="mt-2 text-xs">
                <a href="/qr" className="hover:text-gold transition-colors" data-testid="footer-qr-link">
                  View / Print QR Code
                </a>
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
